import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, ChevronRight, Copy, Mail, RefreshCw, Ban, BellRing } from "lucide-react";
import { IOSButton, Field, TextArea, StatusBadge, InsetList, Sheet } from "@/components/ios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function InvoicePanel({ token }) {
  const [invoices, setInvoices] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const load = () =>
    axios
      .get(`${API}/legal/invoices`, auth)
      .then((res) => setInvoices(res.data.invoices))
      .catch(() => toast.error("Could not load invoices"));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const replace = (inv) => {
    setInvoices((list) => list.map((i) => (i.id === inv.id ? inv : i)));
    setSelected((s) => (s && s.id === inv.id ? inv : s));
  };

  const create = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/legal/invoices`,
        {
          client_name: clientName,
          client_email: clientEmail,
          description,
          amount: parseFloat(amount),
          days_until_due: 14,
        },
        auth
      );
      setInvoices((list) => [res.data, ...(list || [])]);
      setCreateOpen(false);
      setClientName("");
      setClientEmail("");
      setDescription("");
      setAmount("");
      toast.success("Invoice created — payment link ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invoice");
    } finally {
      setBusy(false);
    }
  };

  const removeInvoice = async (inv) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/legal/invoices/${inv.id}`, auth);
      setInvoices((list) => list.filter((i) => i.id !== inv.id));
      setSelected(null);
      toast.success("Invoice deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete invoice");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = (inv) => {
    const url = inv.hosted_invoice_url;
    const done = () => toast.success("Payment link copied");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => window.prompt("Copy payment link", url));
    } else {
      window.prompt("Copy payment link", url);
    }
  };

  const act = async (inv, action) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/legal/invoices/${inv.id}/${action}`, {}, auth);
      replace(res.data);
      if (action === "send") toast.success(`Invoice emailed to ${res.data.client_email}`);
      if (action === "remind") toast.success(`Reminder sent to ${res.data.client_email}`);
      if (action === "sync") toast.success(`Status: ${res.data.status}`);
      if (action === "void") toast.success("Invoice voided");
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action}`);
    } finally {
      setBusy(false);
    }
  };

  const valid = clientName.trim() && clientEmail.includes("@") && description.trim() && parseFloat(amount) >= 0.5;

  return (
    <div data-testid="invoice-panel">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[15px] text-[#3C3C43]/60">Stripe invoices with hosted payment links.</p>
        <IOSButton data-testid="new-invoice-button" onClick={() => setCreateOpen(true)} className="whitespace-nowrap shrink-0">
          <Plus className="w-4 h-4" strokeWidth={2} /> New invoice
        </IOSButton>
      </div>

      {!invoices && (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#3C3C43]/40 animate-spin" strokeWidth={1.5} />
        </div>
      )}
      {invoices && invoices.length === 0 && (
        <div className="bg-white rounded-sm p-10 text-center" data-testid="invoices-empty">
          <p className="text-[15px] text-[#3C3C43]/60">No invoices yet. Create your first one to get a payable Stripe link.</p>
        </div>
      )}
      {invoices && invoices.length > 0 && (
        <InsetList testid="invoice-list">
          {invoices.map((inv, i) => (
            <button
              key={inv.id}
              type="button"
              data-testid={`invoice-row-${i}`}
              onClick={() => setSelected(inv)}
              className="w-full flex items-center gap-4 px-5 py-4 text-left min-h-[64px] active:bg-black/[0.03] transition-colors duration-150"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-[#0A0A0A] truncate">{inv.client_name}</p>
                <p className="text-[13px] text-[#3C3C43]/55 truncate">
                  {inv.number || "Invoice"} · {inv.description}
                </p>
              </div>
              <span className="text-[15px] font-semibold tabular-nums">{money(inv.amount_cents)}</span>
              <StatusBadge status={inv.status} />
              <ChevronRight className="w-4 h-4 text-[#3C3C43]/30" strokeWidth={2} />
            </button>
          ))}
        </InsetList>
      )}

      <Sheet open={createOpen} onClose={() => setCreateOpen(false)} title="New invoice" testid="invoice-create-sheet">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Client name" testid="invoice-client-name-input" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Acme Corp" maxLength={120} />
            <Field label="Client email" testid="invoice-client-email-input" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="ap@acme.com" />
          </div>
          <TextArea label="What is this for?" testid="invoice-description-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Diligence packet processing — March" maxLength={400} />
          <Field label="Amount (USD)" testid="invoice-amount-input" type="number" min="0.5" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="250.00" />
          <p className="text-[13px] text-[#3C3C43]/55">A Stripe invoice is created immediately with a hosted payment page. Due in 14 days.</p>
          <IOSButton data-testid="invoice-create-submit" onClick={create} disabled={!valid || busy} className="w-full">
            {busy ? "Creating…" : "Create invoice"}
          </IOSButton>
        </div>
      </Sheet>

      <Sheet open={!!selected} onClose={() => setSelected(null)} title={selected ? `Invoice ${selected.number || ""}` : ""} testid="invoice-detail-sheet">
        {selected && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold tracking-tight tabular-nums" data-testid="invoice-detail-amount">{money(selected.amount_cents)}</p>
                <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">{selected.client_name} · {selected.client_email}</p>
              </div>
              <StatusBadge status={selected.status} />
            </div>
            <div className="rounded-sm bg-[#F2F2F7] p-4">
              <p className="text-[14px] leading-relaxed text-[#0A0A0A]">{selected.description}</p>
              <p className="text-[12px] text-[#3C3C43]/50 mt-2">
                Created {new Date(selected.created_at).toLocaleString()}
                {selected.email_sent_at && ` · emailed ${new Date(selected.email_sent_at).toLocaleString()}`}
                {selected.last_reminded_at && ` · reminded ${new Date(selected.last_reminded_at).toLocaleString()}`}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <IOSButton variant="plain" data-testid="invoice-copy-link" onClick={() => copyLink(selected)} disabled={!selected.hosted_invoice_url}>
                <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy pay link
              </IOSButton>
              <IOSButton variant="plain" data-testid="invoice-send-email" onClick={() => act(selected, "send")} disabled={busy || selected.status !== "open"}>
                <Mail className="w-4 h-4" strokeWidth={1.5} /> Email client
              </IOSButton>
              <IOSButton variant="plain" data-testid="invoice-remind" onClick={() => act(selected, "remind")} disabled={busy || selected.status !== "open"}>
                <BellRing className="w-4 h-4" strokeWidth={1.5} /> Remind
              </IOSButton>
              <IOSButton variant="plain" data-testid="invoice-sync" onClick={() => act(selected, "sync")} disabled={busy}>
                <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Sync status
              </IOSButton>
              <IOSButton variant="destructive" data-testid="invoice-void" onClick={() => act(selected, "void")} disabled={busy || selected.status !== "open"}>
                <Ban className="w-4 h-4" strokeWidth={1.5} /> Void
              </IOSButton>
            </div>
            {selected.status !== "open" && (
              <IOSButton variant="destructive" data-testid="invoice-delete" onClick={() => removeInvoice(selected)} disabled={busy} className="w-full">
                Delete from workspace
              </IOSButton>
            )}
          </div>
        )}
      </Sheet>
    </div>
  );
}
